/**
 * Customer Pickup in the warehouse app: an order Hub marked for pickup (`Order.customerPickup`) is
 * picked (and verified) as usual, then — on the server, when Complete / Complete Verify commits —
 * set to the Customer Pickup status instead of going to the Shipping step. It never gets a label,
 * carrier or tracking. If that automatic step was refused (e.g. a missing GCP), the order waits at
 * Shipping, labelled Customer Pickup, and the order summary offers Record Customer Pickup (never the
 * Shipping form; the server refuses a carrier ship of a pickup order anyway). See docs/customer-pickup.
 */

/** The status of an order the customer collected — its "shipped". Mirrors CUSTOMER_PICKUP_VENDOR_STATUS. */
export const CUSTOMER_PICKUP_VENDOR_STATUS = 'customer_pickup';

export const CUSTOMER_PICKUP_LABEL = 'Customer Pickup';

/** Shipped, picked up or completed: the order has left. Mirrors SHIPPED_OR_COMPLETED_VENDOR_STATUSES. */
export const SHIPPED_OR_COMPLETED_VENDOR_STATUSES = ['shipped', CUSTOMER_PICKUP_VENDOR_STATUS, 'completed'];

export const isShippedOrCompletedVendorStatus = (vendorStatus?: string | null): boolean =>
  SHIPPED_OR_COMPLETED_VENDOR_STATUSES.includes(String(vendorStatus ?? ''));

export type CustomerPickupOrderLike = {
  vendorStatus?: string | null;
  customerPickup?: { enabled?: boolean | null; markedAt?: string | null; lastError?: string | null } | null;
};

/** Marked for customer pickup (or already picked up): label it, and keep it out of Shipping. */
export const isCustomerPickupOrder = (order?: CustomerPickupOrderLike | null): boolean =>
  !!order &&
  (order.customerPickup?.enabled === true || order.vendorStatus === CUSTOMER_PICKUP_VENDOR_STATUS);

/** Already set to Customer Pickup (paid or not). */
export const isPickedUp = (order?: CustomerPickupOrderLike | null): boolean =>
  !!order && (order.vendorStatus === CUSTOMER_PICKUP_VENDOR_STATUS || !!order.customerPickup?.markedAt);

/**
 * A pickup order still waiting at Shipping: its automatic Customer Pickup did not go through. The
 * summary shows why and offers Record Customer Pickup.
 */
export const isPickupAwaitingRecord = (order?: CustomerPickupOrderLike | null): boolean =>
  !!order && order.vendorStatus === 'shipping' && order.customerPickup?.enabled === true;

/** The toast after Complete / Complete Verify recorded the pickup. */
export const customerPickupRecordedNotice = (vendorSalesOrderNumber?: string | null) =>
  `SO #${vendorSalesOrderNumber ?? ''} set to Customer Pickup — no shipping needed. Hand it to the customer at the counter.`;
